// engine.js - Core game engine with sprite, input, and timer helpers

export class Engine {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', { alpha: false });
        this.width = 320;  // Logical width (8-bit style)
        this.height = 240; // Logical height
        this.setupCanvas();

        // Game loop
        this.lastTime = 0;
        this.deltaTime = 0;
        this.running = false;
        this.updateCallback = null;
        this.renderCallback = null;

        // Input state
        this.input = {
            mouse: { x: 0, y: 0, down: false, clicked: false },
            touch: { x: 0, y: 0, active: false, tapped: false },
            keys: {}
        };

        // Sprite and animation helpers
        this.sprites = new Map();
        this.animations = [];

        this.setupInput();
    }

    setupCanvas() {
        // Set internal resolution (logical pixels)
        this.canvas.width = this.width;
        this.canvas.height = this.height;

        // Disable image smoothing for pixel-perfect rendering
        this.ctx.imageSmoothingEnabled = false;
        this.ctx.mozImageSmoothingEnabled = false;
        this.ctx.webkitImageSmoothingEnabled = false;
        this.ctx.msImageSmoothingEnabled = false;

        // Scale canvas to fit container while maintaining aspect ratio
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());
    }

    resizeCanvas() {
        const container = this.canvas.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate scale to fit container while maintaining aspect ratio
        const scale = Math.min(
            containerWidth / this.width,
            containerHeight / this.height
        );

        // Apply CSS scaling
        this.canvas.style.width = `${this.width * scale}px`;
        this.canvas.style.height = `${this.height * scale}px`;

        // Store scale for input coordinate conversion
        this.scale = scale;
    }

    setupInput() {
        // Mouse events
        this.canvas.addEventListener('mousedown', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.input.mouse.x = (e.clientX - rect.left) * (this.width / rect.width);
            this.input.mouse.y = (e.clientY - rect.top) * (this.height / rect.height);
            this.input.mouse.down = true;
            this.input.mouse.clicked = true;
        });

        this.canvas.addEventListener('mouseup', () => {
            this.input.mouse.down = false;
        });

        this.canvas.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.input.mouse.x = (e.clientX - rect.left) * (this.width / rect.width);
            this.input.mouse.y = (e.clientY - rect.top) * (this.height / rect.height);
        });

        // Touch events
        this.canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const rect = this.canvas.getBoundingClientRect();
            this.input.touch.x = (touch.clientX - rect.left) * (this.width / rect.width);
            this.input.touch.y = (touch.clientY - rect.top) * (this.height / rect.height);
            this.input.touch.active = true;
            this.input.touch.tapped = true;
        });

        this.canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.input.touch.active = false;
        });

        this.canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const rect = this.canvas.getBoundingClientRect();
            this.input.touch.x = (touch.clientX - rect.left) * (this.width / rect.width);
            this.input.touch.y = (touch.clientY - rect.top) * (this.height / rect.height);
        });

        // Keyboard events
        window.addEventListener('keydown', (e) => {
            this.input.keys[e.key] = true;
        });

        window.addEventListener('keyup', (e) => {
            this.input.keys[e.key] = false;
        });
    }

    // Input helpers
    isPointerDown() {
        return this.input.mouse.down || this.input.touch.active;
    }

    wasPointerClicked() {
        const clicked = this.input.mouse.clicked || this.input.touch.tapped;
        // Reset click/tap state after checking
        this.input.mouse.clicked = false;
        this.input.touch.tapped = false;
        return clicked;
    }

    getPointerPosition() {
        return this.input.touch.active
            ? { x: this.input.touch.x, y: this.input.touch.y }
            : { x: this.input.mouse.x, y: this.input.mouse.y };
    }

    isKeyDown(key) {
        return this.input.keys[key] || false;
    }

    // Collision detection
    pointInRect(px, py, rect) {
        return px >= rect.x && px <= rect.x + rect.width &&
               py >= rect.y && py <= rect.y + rect.height;
    }

    rectsCollide(rect1, rect2) {
        return rect1.x < rect2.x + rect2.width &&
               rect1.x + rect1.width > rect2.x &&
               rect1.y < rect2.y + rect2.height &&
               rect1.y + rect1.height > rect2.y;
    }

    // Drawing helpers
    clear(color = '#1a1a1a') {
        this.ctx.fillStyle = color;
        this.ctx.fillRect(0, 0, this.width, this.height);
    }

    drawRect(x, y, width, height, color) {
        this.ctx.fillStyle = color;
        this.ctx.fillRect(Math.floor(x), Math.floor(y), width, height);
    }

    drawRectOutline(x, y, width, height, color, lineWidth = 2) {
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = lineWidth;
        this.ctx.strokeRect(Math.floor(x), Math.floor(y), width, height);
    }

    drawCircle(x, y, radius, color) {
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.arc(Math.floor(x), Math.floor(y), radius, 0, Math.PI * 2);
        this.ctx.fill();
    }

    drawText(text, x, y, size = 8, color = '#F4E8C1', align = 'left') {
        this.ctx.fillStyle = color;
        this.ctx.font = `${size}px monospace`;
        this.ctx.textAlign = align;
        this.ctx.textBaseline = 'top';
        this.ctx.fillText(text, Math.floor(x), Math.floor(y));
    }

    // Draw text with automatic wrapping
    drawWrappedText(text, x, y, maxWidth, size = 8, color = '#F4E8C1', align = 'left', lineHeight = 1.5) {
        this.ctx.fillStyle = color;
        this.ctx.font = `${size}px monospace`;
        this.ctx.textAlign = align;
        this.ctx.textBaseline = 'top';

        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        // Build lines that fit within maxWidth
        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = this.ctx.measureText(testLine);

            if (metrics.width > maxWidth && currentLine) {
                // Line is too long, push current line and start new one
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = testLine;
            }
        }

        // Push the last line
        if (currentLine) {
            lines.push(currentLine);
        }

        // Calculate starting Y position based on alignment
        let currentY = y;
        const totalHeight = lines.length * size * lineHeight;

        // Adjust X based on alignment for each line
        let adjustedX = x;
        if (align === 'center') {
            adjustedX = x; // X is already the center point
        } else if (align === 'right') {
            adjustedX = x; // X is already the right edge
        }

        // Draw each line
        lines.forEach((line, index) => {
            const lineY = currentY + (index * size * lineHeight);
            this.ctx.fillText(line, Math.floor(adjustedX), Math.floor(lineY));
        });

        // Return the total height used for potential layout adjustments
        return totalHeight;
    }

    // Sprite management
    loadSprite(name, imageSrc) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.sprites.set(name, img);
                resolve(img);
            };
            img.onerror = reject;
            img.src = imageSrc;
        });
    }

    drawSprite(name, x, y, width = null, height = null) {
        const sprite = this.sprites.get(name);
        if (!sprite) return;

        if (width === null) width = sprite.width;
        if (height === null) height = sprite.height;

        this.ctx.drawImage(
            sprite,
            Math.floor(x),
            Math.floor(y),
            width,
            height
        );
    }

    // Draw a sub-section of a sprite (for sprite sheets)
    drawSpriteFrame(name, frameX, frameY, frameWidth, frameHeight, x, y, width = null, height = null) {
        const sprite = this.sprites.get(name);
        if (!sprite) return;

        if (width === null) width = frameWidth;
        if (height === null) height = frameHeight;

        this.ctx.drawImage(
            sprite,
            frameX, frameY, frameWidth, frameHeight,
            Math.floor(x), Math.floor(y), width, height
        );
    }

    // Animation helper
    createAnimation(frames, frameDuration) {
        return {
            frames,
            frameDuration,
            currentFrame: 0,
            elapsed: 0,
            loop: true,
            active: true
        };
    }

    updateAnimation(animation, deltaTime) {
        if (!animation.active) return;

        animation.elapsed += deltaTime;
        if (animation.elapsed >= animation.frameDuration) {
            animation.elapsed = 0;
            animation.currentFrame++;

            if (animation.currentFrame >= animation.frames.length) {
                if (animation.loop) {
                    animation.currentFrame = 0;
                } else {
                    animation.currentFrame = animation.frames.length - 1;
                    animation.active = false;
                }
            }
        }
    }

    getCurrentFrame(animation) {
        return animation.frames[animation.currentFrame];
    }

    // Timer helper
    createTimer(duration, onComplete) {
        return {
            duration,
            elapsed: 0,
            active: true,
            onComplete
        };
    }

    updateTimer(timer, deltaTime) {
        if (!timer.active) return;

        timer.elapsed += deltaTime;
        if (timer.elapsed >= timer.duration) {
            timer.active = false;
            if (timer.onComplete) {
                timer.onComplete();
            }
        }
    }

    getTimerProgress(timer) {
        return Math.min(timer.elapsed / timer.duration, 1);
    }

    getTimerRemaining(timer) {
        return Math.max(0, timer.duration - timer.elapsed);
    }

    // Game loop
    start(updateCallback, renderCallback) {
        this.updateCallback = updateCallback;
        this.renderCallback = renderCallback;
        this.running = true;
        this.lastTime = performance.now();
        requestAnimationFrame((time) => this.loop(time));
    }

    stop() {
        this.running = false;
    }

    loop(currentTime) {
        if (!this.running) return;

        // Calculate delta time in seconds
        this.deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        // Cap delta time to prevent large jumps
        if (this.deltaTime > 0.1) this.deltaTime = 0.1;

        // Update
        if (this.updateCallback) {
            this.updateCallback(this.deltaTime);
        }

        // Render
        this.clear();
        if (this.renderCallback) {
            this.renderCallback(this.ctx);
        }

        requestAnimationFrame((time) => this.loop(time));
    }

    // Utility functions
    randomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    randomFloat(min, max) {
        return Math.random() * (max - min) + min;
    }

    randomChoice(array) {
        return array[Math.floor(Math.random() * array.length)];
    }

    clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
    }

    lerp(start, end, t) {
        return start + (end - start) * t;
    }

    distance(x1, y1, x2, y2) {
        const dx = x2 - x1;
        const dy = y2 - y1;
        return Math.sqrt(dx * dx + dy * dy);
    }
}

// Particle system for effects
export class ParticleSystem {
    constructor(engine) {
        this.engine = engine;
        this.particles = [];
    }

    emit(x, y, count, options = {}) {
        const defaults = {
            color: '#D4AF37',
            size: 2,
            speed: 50,
            lifetime: 1,
            gravity: 0,
            spread: 360
        };

        const config = { ...defaults, ...options };

        for (let i = 0; i < count; i++) {
            const angle = (Math.random() * config.spread - config.spread / 2) * Math.PI / 180;
            const speed = config.speed * (0.5 + Math.random() * 0.5);

            this.particles.push({
                x, y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                color: config.color,
                size: config.size,
                lifetime: config.lifetime,
                age: 0,
                gravity: config.gravity
            });
        }
    }

    update(deltaTime) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            p.age += deltaTime;
            if (p.age >= p.lifetime) {
                this.particles.splice(i, 1);
                continue;
            }

            p.vy += p.gravity * deltaTime;
            p.x += p.vx * deltaTime;
            p.y += p.vy * deltaTime;
        }
    }

    render(ctx) {
        for (const p of this.particles) {
            const alpha = 1 - (p.age / p.lifetime);
            this.engine.ctx.globalAlpha = alpha;
            this.engine.drawCircle(p.x, p.y, p.size, p.color);
        }
        this.engine.ctx.globalAlpha = 1;
    }

    clear() {
        this.particles = [];
    }
}
